import React from 'react'
import { Prompt, Redirect, withRouter } from 'react-router-dom'
import moment from 'moment'
import Header from '../containers/HeaderContainer'
import PublicPreviewModal from './PublicPreviewModal'

const BackIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 6 9 12l6 6" />
    </svg>
)

const FIELD_TIPS = {
    completed: 'Event has been drawn. Ticket sales stop and the public page shows the final result.',
    pending: 'Event is not started yet. Ticket sales are blocked and the public page shows a not-started message.',
    drawPending: 'Ticket sales are closed because the event is waiting for a manual draw. Manual draw is still available.',
    manualDraw: 'Prevents automatic drawing when the max prize or draw date threshold is reached.',
    addressRequired: 'Requires winner address details when checking or managing purchase records.',
    completeBeforeDraw: 'When a draw threshold is reached, close ticket sales first and wait for a manual draw instead of drawing automatically.',
    altDrawMsg: 'Public message shown while the event is live instead of the default draw-threshold message.',
    completedAltText: 'Public message shown after completion when Complete before draw is enabled.'
}

class EditEvent extends React.Component {
    suppressUnsavedPrompt = false

    state = {
        busy: false,
        savingEvent: false,
        drawingEvent: false,
        checkingUser: true,
        data: null,
        initialSnapshot: '',
        activeTab: 'details',
        previewOpen: false,
        eventBundleRows: [],
        notice: '',
        winnerDetails: null,
        winnerLookupComplete: false,
        purchaseDrafts: {},
        searchingPurchases: false,
        savingPurchaseKey: '',
        refundingPurchaseKey: '',
        searchPhone: ''
    }

    componentDidMount() {
        window.addEventListener('beforeunload', this._handleBeforeUnload)
        if (!this.props.user) {
            this.setState({ checkingUser: true }, () => {
                this.props.checkUserSession(() => {
                    this.setState({ checkingUser: false })
                    this._fetchEvent()
                })
            })
        } else {
            this.setState({ checkingUser: false }, () => this._fetchEvent())
        }
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this._handleBeforeUnload)
    }

    _pathParts() {
        const pathArr = this.props.location.pathname.split('/')
        return {
            clientId: pathArr[pathArr.length - 3],
            eventId: pathArr[pathArr.length - 2]
        }
    }

    _fetchEvent() {
        const ids = this._pathParts()
        this.setState({ busy: true }, () => {
            this.props.fetchClientAndEvent(ids.clientId, ids.eventId, (data) => {
                const nextEvent = data && data.event ? this._clone(data.event) : null
                const nextEventBundleRows = this._bundleRowsFromObject(nextEvent ? nextEvent.bundles : {})
                this.setState({
                    busy: false,
                    activeTab: 'details',
                    data: data ? {
                        client: data.client,
                        event: nextEvent
                    } : null,
                    eventBundleRows: nextEventBundleRows,
                    initialSnapshot: data && nextEvent ? this._normalizedEventDraft({
                        client: data.client,
                        event: nextEvent
                    }, nextEventBundleRows) : '',
                    notice: '',
                    winnerDetails: null,
                    winnerLookupComplete: false
                }, () => {
                    this.suppressUnsavedPrompt = false
                    if (nextEvent && nextEvent.completed && nextEvent.winningTicket && nextEvent.winningTicket !== '-') {
                        this._fetchWinnerDetails(data.client, ids.eventId, nextEvent.winningTicket)
                    }
                })
            })
        })
    }

    _bundleRowsFromObject(bundles) {
        return Object.keys(bundles || {})
            .sort((a, b) => Number(a) - Number(b))
            .map((price) => ({
                price: String(price),
                count: bundles[price] === undefined || bundles[price] === null ? '' : String(bundles[price])
            }))
    }

    _bundleObjectFromRows(rows) {
        const bundles = {}
        ;(rows || []).forEach((row) => {
            const price = String(row.price || '').trim()
            const count = row.count
            if (price === '' || count === '' || count === null || count === undefined) return
            bundles[price] = count
        })
        return bundles
    }

    _normalizedEventDraft(data = this.state.data, eventBundleRows = this.state.eventBundleRows) {
        if (!data || !data.event) return ''
        const normalized = this._clone(data.event)
        normalized.bundles = this._bundleObjectFromRows(eventBundleRows)
        normalized.drawDate = this._fromDateTimeInput(normalized.drawDate)
        normalized.startDate = this._fromDateTimeInput(normalized.startDate)
        normalized.endDate = this._fromDateTimeInput(normalized.endDate)
        return JSON.stringify(normalized)
    }

    _isDirty() {
        if (this.suppressUnsavedPrompt) return false
        return !!this.state.data && this._normalizedEventDraft() !== this.state.initialSnapshot
    }

    _handleBeforeUnload = (event) => {
        if (!this._isDirty()) return
        event.preventDefault()
        event.returnValue = ''
    }

    _fetchWinnerDetails(client, eventId, winningTicket) {
        if (!client || !client.PK) {
            this.setState({ winnerDetails: null, winnerLookupComplete: true })
            return
        }

        this.props.fetchEventReport(client.PK, eventId, (response) => {
            const purchases = response && response.purchases ? response.purchases : []
            const winningTicketNumber = Number(winningTicket)
            const winner = purchases.find((purchase) => {
                const start = Number(purchase.ticketIndex)
                const count = Number(purchase.ticketCount || 0)
                const end = start + count - 1
                return Number.isFinite(start) && Number.isFinite(end) && winningTicketNumber >= start && winningTicketNumber <= end
            }) || null

            this.setState({
                winnerDetails: winner,
                winnerLookupComplete: true
            })
        })
    }

    _clone(value) {
        return JSON.parse(JSON.stringify(value))
    }

    _updateEventField(field, value) {
        const data = this._clone(this.state.data)
        data.event[field] = value
        this.setState({ data })
    }

    _updateBundleRow(index, field, value) {
        const eventBundleRows = this.state.eventBundleRows.map((row, rowIndex) => (
            rowIndex === index ? { ...row, [field]: value } : row
        ))
        const data = this._clone(this.state.data)
        data.event.bundles = this._bundleObjectFromRows(eventBundleRows)
        this.setState({ data, eventBundleRows })
    }

    _addBundleRow() {
        this.setState({ eventBundleRows: [...this.state.eventBundleRows, { price: '', count: '' }] })
    }

    _removeBundleRow(index) {
        const eventBundleRows = this.state.eventBundleRows.filter((_, rowIndex) => rowIndex !== index)
        const data = this._clone(this.state.data)
        data.event.bundles = this._bundleObjectFromRows(eventBundleRows)
        this.setState({ data, eventBundleRows })
    }

    _saveEvent() {
        const ids = this._pathParts()
        const event = this._clone(this.state.data.event)
        event.bundles = this._bundleObjectFromRows(this.state.eventBundleRows)
        event.drawDate = this._fromDateTimeInput(event.drawDate)
        event.startDate = this._fromDateTimeInput(event.startDate)
        event.endDate = this._fromDateTimeInput(event.endDate)

        this.setState({ savingEvent: true, notice: '' }, () => {
            this.props.updateEvent({
                clientId: ids.clientId,
                eventId: ids.eventId,
                event
            }, (response) => {
                if (response && response.error) {
                    this.suppressUnsavedPrompt = false
                } else {
                    this.suppressUnsavedPrompt = true
                }
                this.setState({
                    savingEvent: false,
                    notice: response && response.error ? response.message : 'Event updated successfully.'
                }, () => {
                    if (!response || !response.error) this._fetchEvent()
                })
            })
        })
    }

    _manualDraw() {
        const ids = this._pathParts()
        if (!window.confirm('Draw this event now?')) return
        this.setState({ drawingEvent: true, notice: '' }, () => {
            this.props.manualDraw({
                clientId: ids.clientId,
                eventId: ids.eventId
            }, (response) => {
                const message = response && response.error ? response.message : 'Manual draw requested.'
                this.setState({ drawingEvent: false, notice: message }, () => this._fetchEvent())
            })
        })
    }

    _searchPurchases() {
        const ids = this._pathParts()
        if (!this.state.searchPhone) {
            this.setState({ notice: 'Enter a phone number to search purchases.' })
            return
        }

        this.setState({ searchingPurchases: true, notice: '' }, () => {
            this.props.fetchEventReport(ids.clientId, ids.eventId, (response) => {
                const purchases = this._filterPurchasesByPhone(response && response.purchases ? response.purchases : [], this.state.searchPhone)
                const drafts = {}
                ;(purchases || []).forEach((purchase) => {
                    drafts[purchase.SK] = this._clone(purchase)
                })
                this.setState({
                    searchingPurchases: false,
                    purchaseDrafts: drafts,
                    notice: purchases && purchases.length > 0 ? '' : 'No purchases found for that phone number in this event.'
                })
            })
        })
    }

    _normalizePhone(value) {
        if (!value) return ''
        let digits = String(value).replace(/[^\d]/g, '')
        if (digits.length === 9 && digits.indexOf('4') === 0) digits = '0' + digits
        if (digits.length === 11 && digits.indexOf('61') === 0 && digits.charAt(2) === '4') digits = '0' + digits.substring(2)
        return digits
    }

    _filterPurchasesByPhone(purchases, phone) {
        const target = this._normalizePhone(phone)
        if (!target) return []
        return (purchases || []).filter((purchase) => this._normalizePhone(purchase.phone) === target)
    }

    _updatePurchaseField(key, field, value) {
        const purchaseDrafts = this._clone(this.state.purchaseDrafts)
        purchaseDrafts[key][field] = field === 'addressState' ? String(value || '').toUpperCase() : value
        this.setState({ purchaseDrafts })
    }

    _savePurchase(key) {
        const ids = this._pathParts()
        const purchase = this._clone(this.state.purchaseDrafts[key])
        this.setState({ savingPurchaseKey: key, notice: '' }, () => {
            this.props.updatePurchase({
                eventId: ids.eventId,
                purchaseKey: key,
                purchase
            }, (response) => {
                this.setState({
                    savingPurchaseKey: '',
                    notice: response && response.error ? response.message : 'Purchase updated successfully.'
                }, () => {
                    if (!response || !response.error) this._searchPurchases()
                })
            })
        })
    }

    _adminEmail() {
        return this.props.user && this.props.user.email ? this.props.user.email : ''
    }

    _refundPurchase(key) {
        const ids = this._pathParts()
        const event = this.state.data && this.state.data.event ? this.state.data.event : {}
        const purchase = this.state.purchaseDrafts[key]
        if (event.completed) return
        if (!purchase || this._refundLabel(purchase)) return

        const confirmed = window.confirm(
            'Refund this purchase in full?\n\n' +
            'This will call PIN, mark the purchase as refunded, keep the original ticket range on the purchase, ' +
            'and exclude those tickets from any future winner draw for this event.'
        )
        if (!confirmed) return

        this.setState({ refundingPurchaseKey: key, notice: '' }, () => {
            this.props.refundPurchase({
                clientId: ids.clientId,
                eventId: ids.eventId,
                purchaseKey: key,
                refundedBy: this._adminEmail()
            }, (response) => {
                this.setState({
                    refundingPurchaseKey: '',
                    notice: response && response.error ? response.message : 'Purchase refunded successfully.'
                }, () => {
                    if (!response || !response.error) this._searchPurchases()
                })
            })
        })
    }

    _ticketRangeLabel(eventId, purchase) {
        if (!purchase || purchase.ticketIndex === undefined || purchase.ticketIndex === null) return 'Unknown'
        const start = Number(purchase.ticketIndex)
        const count = Number(purchase.ticketCount || 0)
        if (!Number.isFinite(start)) return 'Unknown'
        if (!Number.isFinite(count) || count <= 1) return eventId + '_' + start
        return eventId + '_' + start + ' - ' + eventId + '_' + (start + count - 1)
    }

    _refundLabel(purchase) {
        if (!purchase) return ''
        if (purchase.refunded === true) return 'Refunded'
        if (purchase.refund === true) return 'Refunded'
        if (purchase.refundedAt) return 'Refunded'
        if (purchase.refundDate) return 'Refunded'
        if (purchase.refundId) return 'Refunded'
        if (purchase.status && String(purchase.status).toLowerCase() === 'refunded') return 'Refunded'
        return ''
    }

    _toDateTimeInput(value) {
        if (!value) return ''
        const date = moment(value)
        return date.isValid() ? date.format('YYYY-MM-DDTHH:mm') : ''
    }

    _fromDateTimeInput(value) {
        if (!value) return ''
        const date = moment(value)
        return date.isValid() ? date.toDate().toISOString() : ''
    }

    _field(label, field, options = {}) {
        const event = this.state.data.event
        const value = event[field] === undefined || event[field] === null ? '' : event[field]
        const inputClass = 'admin-input mt-2 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-500 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-400'
        const readOnlyClass = 'admin-input mt-2 block w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500 shadow-sm outline-none dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400'

        return (
            <label className={options.className || ''}>
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
                    <span>{label}</span>
                    {FIELD_TIPS[field] ? this._tip(FIELD_TIPS[field]) : null}
                </div>
                {options.textarea ?
                    <textarea className={inputClass} rows={options.rows || 3} value={value} onChange={(e) => this._updateEventField(field, e.target.value)} />
                    : options.readOnly ?
                    <input className={readOnlyClass} type={options.type || 'text'} value={value} readOnly />
                    :
                    <input className={inputClass} type={options.type || 'text'} min={options.min} step={options.step} value={value} onChange={(e) => this._updateEventField(field, e.target.value)} />
                }
            </label>
        )
    }

    _tip(text) {
        return (
            <span className="group relative inline-flex">
                <button type="button" className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-[11px] font-bold text-slate-500 shadow-sm transition hover:border-sky-400 hover:text-sky-600 focus:border-sky-400 focus:text-sky-600 focus:outline-none dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
                    ?
                </button>
                <span className="pointer-events-none absolute left-1/2 top-7 z-50 hidden w-72 -translate-x-1/2 rounded-xl bg-slate-950 px-3 py-2 text-left text-xs font-medium leading-5 text-white shadow-xl group-hover:block group-focus-within:block dark:bg-white dark:text-slate-950">
                    {text}
                </span>
            </span>
        )
    }

    render() {
        if (this.state.checkingUser || (this.state.busy && !this.state.data)) {
            return <div className="px-4 py-5 text-center">Loading event workspace...</div>
        }

        if (!this.props.user) {
            return <Redirect to='/' />
        }

        if (!this.state.data || !this.state.data.event || !this.state.data.client) {
            return (
                <div className="min-h-screen lg:flex">
                    <Header />
                    <div className="px-4 py-5 text-center">Event not found.</div>
                </div>
            )
        }

        const client = this.state.data.client
        const event = this.state.data.event
        const purchaseKeys = Object.keys(this.state.purchaseDrafts)
        const isLive = !event.completed
        const winnerDetails = this.state.winnerDetails
        const winnerLookupComplete = this.state.winnerLookupComplete
        const searchingPurchases = this.state.searchingPurchases
        const ids = this._pathParts()
        const inputClass = 'admin-input mt-2 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-500 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-400'
        const isDirty = this._isDirty()

        return (
            <div className="min-h-screen lg:flex">
                <Prompt
                    when={isDirty}
                    message="You have unsaved event changes. Leave this page without saving?"
                />
                <Header />
                <main className="min-w-0 flex-1 px-4 py-4 pb-32 sm:px-6 lg:px-10 lg:py-8 lg:pb-36">
                    <div className="mx-auto max-w-7xl">
                        <div className="rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                                <div className="max-w-3xl">
                                    <a href={'/events/' + client.PK} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.28em] text-sky-600 no-underline dark:text-sky-300">{BackIcon} Back to events</a>
                                    <div className="mt-3 flex flex-wrap items-center gap-3">
                                        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-4xl">{client.name} · {event.SK}</h1>
                                        <span className={'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ' + (isLive
                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300'
                                            : 'bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300')}>
                                            {isLive ? 'Live' : 'Completed'}
                                        </span>
                                    </div>
                                    <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300 sm:text-base">
                                        Edit the event record with more appropriate controls, then handle purchase corrections by phone search in the panel alongside it.
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    <button className="inline-flex items-center justify-center rounded-2xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900" onClick={() => this._fetchEvent()}>
                                        Reload
                                    </button>
                                </div>
                            </div>
                            {this.state.notice ? <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-200">{this.state.notice}</div> : null}
                        </div>

                        <div className="admin-tab-strip mt-6 inline-flex rounded-2xl border border-slate-200 bg-white/90 p-1 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                            {[
                                ['details', 'Details'],
                                ['actions', 'Actions'],
                                ['purchases', 'Purchases']
                            ].map((tab) => (
                                <button
                                    key={tab[0]}
                                    onClick={() => this.setState({ activeTab: tab[0] })}
                                    className={'rounded-xl px-4 py-2 text-sm font-semibold transition ' + (this.state.activeTab === tab[0]
                                        ? 'bg-slate-900 text-white dark:bg-sky-500 dark:text-slate-950'
                                        : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-900')}
                                >
                                    {tab[1]}
                                </button>
                            ))}
                        </div>

                        {this.state.activeTab === 'details' ?
                            <section className="mt-6 rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Event fields</h2>
                                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Structured controls for the fields the raffle front-end depends on today.</p>
                                </div>

                                {event.completed && event.winningTicket && event.winningTicket !== '-' ?
                                    <div className="mt-6 rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-500/20 dark:bg-emerald-500/10">
                                        <div className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-300">Winner</div>
                                        <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                                            <div>
                                                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600/80 dark:text-emerald-300/80">Winning ticket</div>
                                                <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{event.SK}_{event.winningTicket}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600/80 dark:text-emerald-300/80">Winner phone</div>
                                                <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{winnerDetails && winnerDetails.phone ? winnerDetails.phone : (winnerLookupComplete ? 'Not found' : 'Loading...')}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600/80 dark:text-emerald-300/80">Winner email</div>
                                                <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{winnerDetails && winnerDetails.email ? winnerDetails.email : (winnerLookupComplete ? 'Not provided' : 'Loading...')}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600/80 dark:text-emerald-300/80">Purchase range</div>
                                                <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
                                                    {winnerDetails && winnerDetails.ticketIndex
                                                        ? event.SK + '_' + winnerDetails.ticketIndex + (Number(winnerDetails.ticketCount || 0) > 1 ? ' - ' + event.SK + '_' + (Number(winnerDetails.ticketIndex) + Number(winnerDetails.ticketCount || 0) - 1) : '')
                                                        : (winnerLookupComplete ? 'Not found' : 'Loading...')}
                                                </div>
                                            </div>
                                        </div>
                                        {winnerDetails && (winnerDetails.addressNumber || winnerDetails.addressStreet || winnerDetails.addressSuburb || winnerDetails.addressState || winnerDetails.addressPostcode) ?
                                            <div className="mt-4 text-sm text-slate-700 dark:text-slate-200">
                                                {[
                                                    winnerDetails.addressNumber,
                                                    winnerDetails.addressStreet,
                                                    winnerDetails.addressSuburb,
                                                    winnerDetails.addressState,
                                                    winnerDetails.addressPostcode
                                                ].filter(Boolean).join(' ')}
                                            </div>
                                            : null}
                                    </div>
                                    : null}

                                <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                                    {this._field('Title', 'title')}
                                    {this._field('License', 'license')}
                                    {this._field('Winning ticket', 'winningTicket', { readOnly: true })}
                                    {this._field('Prize pool ($)', 'prizePool', { type: 'number', readOnly: true })}
                                    {this._field('Max prize pool ($)', 'maxPrizePool', { type: 'number', min: '0', step: '0.01' })}
                                    {this._field('Ticket count', 'ticketCount', { type: 'number', readOnly: true })}
                                    <label>
                                        <div className="text-sm font-semibold text-slate-900 dark:text-white">Start date</div>
                                        <input className={inputClass} type="datetime-local" value={this._toDateTimeInput(event.startDate)} onChange={(e) => this._updateEventField('startDate', e.target.value)} />
                                    </label>
                                    {event.completed || event.endDate ?
                                        <label>
                                            <div className="text-sm font-semibold text-slate-900 dark:text-white">End date</div>
                                            <input className={inputClass} type="datetime-local" value={this._toDateTimeInput(event.endDate)} onChange={(e) => this._updateEventField('endDate', e.target.value)} />
                                        </label>
                                        : null}
                                    {this._field('Alt draw message', 'altDrawMsg', { textarea: true, rows: 4, className: 'md:col-span-2 xl:col-span-1' })}
                                    {this._field('Completed alt text', 'completedAltText', { textarea: true, rows: 4, className: 'md:col-span-2 xl:col-span-2' })}
                                </div>

                                <div className="mt-6 rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900">
                                    <div className="text-sm font-semibold text-slate-900 dark:text-white">Status and draw rules</div>
                                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                                        {[
                                            ['completed', 'Completed'],
                                            ['pending', 'Pending'],
                                            ['drawPending', 'Draw pending'],
                                            ['manualDraw', 'Manual draw only'],
                                            ['addressRequired', 'Address required'],
                                            ['completeBeforeDraw', 'Complete before draw']
                                        ].map((item) => (
                                            <label key={item[0]} className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
                                                <input type="checkbox" checked={!!event[item[0]]} onChange={(e) => this._updateEventField(item[0], e.target.checked)} />
                                                <span className="flex-1">{item[1]}</span>
                                                {this._tip(FIELD_TIPS[item[0]])}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-6 rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900">
                                    <div className="text-sm font-semibold text-slate-900 dark:text-white">Bundles</div>
                                    <div className="mt-4 space-y-3">
                                        {this.state.eventBundleRows.map((row, index) => (
                                            <div key={'event-bundle-' + index} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-950">
                                                <div className="shrink-0 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Price ($)</div>
                                                <input className={inputClass + ' mt-0 flex-1'} type="number" min="0" step="0.01" value={row.price} onChange={(e) => this._updateBundleRow(index, 'price', e.target.value)} />
                                                <div className="shrink-0 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Ticket count</div>
                                                <input className={inputClass + ' mt-0 flex-1'} type="number" min="0" step="1" value={row.count} onChange={(e) => this._updateBundleRow(index, 'count', e.target.value)} />
                                                <div className="flex shrink-0 items-center justify-end">
                                                    <button type="button" className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900" onClick={() => this._removeBundleRow(index)}>
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        <button type="button" className="inline-flex items-center justify-center rounded-xl border border-dashed border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900" onClick={() => this._addBundleRow()}>
                                            Add bundle
                                        </button>
                                    </div>
                                </div>
                            </section>
                            : null}

                        {this.state.activeTab === 'actions' ?
                            <section className="mt-6 rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Actions</h2>
                                    <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                                        Keep draw triggers and one-off operational actions together for this event.
                                    </p>
                                </div>

                                <div className="mt-5 rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
                                    <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">Draw trigger</div>
                                    <label className="admin-dark-card mt-3 inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
                                        <input
                                            type="checkbox"
                                            checked={!event.manualDraw}
                                            onChange={(e) => this._updateEventField('manualDraw', !e.target.checked)}
                                        />
                                        Scheduled draw
                                    </label>
                                    {event.manualDraw ?
                                        <div className="mt-3">
                                            <div className="text-sm font-semibold text-slate-900 dark:text-white">Manual draw only</div>
                                            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">This event will not auto-draw when thresholds or dates are reached.</div>
                                        </div>
                                        :
                                        <div className="mt-3 space-y-3">
                                            <label>
                                                <div className="text-sm font-semibold text-slate-900 dark:text-white">Draw date</div>
                                                <input className={inputClass + ' mt-2 color-scheme-dark'} type="datetime-local" value={this._toDateTimeInput(event.drawDate)} onChange={(e) => this._updateEventField('drawDate', e.target.value)} />
                                            </label>
                                            <div>
                                                <div className="text-sm font-semibold text-slate-900 dark:text-white">Max prize trigger</div>
                                                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                                    Draw when the prize pool reaches ${Number(event.maxPrizePool || 0).toFixed(2)}.
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-sm font-semibold text-slate-900 dark:text-white">Time trigger</div>
                                                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                                    {event.drawDate ? moment(event.drawDate).format('DD MMM YYYY h:mma') : 'No draw date set.'}
                                                </div>
                                            </div>
                                        </div>}
                                    {event.completed ?
                                        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200">
                                            Event drawn {event.endDate ? moment(event.endDate).format('DD MMM YYYY h:mma') : 'and completed.'}
                                        </div>
                                        : null}
                                    <div className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
                                        <code>drawDate</code> is what auto-draw listens to. <code>endDate</code> is just the completion timestamp and does not trigger a draw.
                                    </div>
                                </div>

                                {!client.rolling && !event.completed ?
                                    <button className="mt-5 inline-flex w-full items-center justify-center rounded-2xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300" onClick={() => this._manualDraw()}>
                                        {this.state.drawingEvent ?
                                            <span className="inline-flex items-center gap-2">
                                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-rose-700/25 border-t-rose-700 dark:border-rose-300/25 dark:border-t-rose-300" />
                                                Drawing...
                                            </span>
                                            :
                                            'Manual draw'}
                                    </button>
                                    : null}
                            </section>
                            : null}

                        {this.state.activeTab === 'purchases' ?
                            <section className="mt-6 rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                                    <div>
                                        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Purchase support</h2>
                                        <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                                            Search this event by phone number, list the matching ticket purchases, and correct customer details without leaving the event screen.
                                        </p>
                                    </div>

                                    <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                                        <input className={inputClass + ' mt-0 flex-1'} type="tel" placeholder="+614..." value={this.state.searchPhone} onChange={(e) => this.setState({ searchPhone: e.target.value })} />
                                        <button className="inline-flex items-center justify-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-sky-500 dark:text-slate-950 dark:hover:bg-sky-400" disabled={searchingPurchases} onClick={() => this._searchPurchases()}>
                                            {searchingPurchases ?
                                                <span className="inline-flex items-center gap-2">
                                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white dark:border-slate-950/25 dark:border-t-slate-950" />
                                                    Searching...
                                                </span>
                                                :
                                                'Search'}
                                        </button>
                                    </div>

                                    <div className="mt-6 space-y-4">
                                        {purchaseKeys.length === 0 ? <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">No purchase records loaded yet.</div> : null}
                                        {purchaseKeys.map((key) => {
                                            const purchase = this.state.purchaseDrafts[key]
                                            const eventDrawn = !!event.completed
                                            return (
                                                <div key={key} className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div>
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <div className="text-sm font-semibold text-slate-900 dark:text-white">{this._ticketRangeLabel(ids.eventId, purchase)} · {purchase.ticketCount} tickets</div>
                                                                {this._refundLabel(purchase) ?
                                                                    <span className="inline-flex items-center rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
                                                                        {this._refundLabel(purchase)}
                                                                    </span>
                                                                    : null}
                                                            </div>
                                                            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                                                Created {purchase.dateCreated ? moment(purchase.dateCreated).format('DD MMM YYYY h:mma') : 'Unknown'} · {purchase.transactionId}
                                                            </div>
                                                            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                                                Ticket range: {this._ticketRangeLabel(ids.eventId, purchase)}
                                                            </div>
                                                            {purchase.refundedAt ?
                                                                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                                                    Refunded {moment(purchase.refundedAt).format('DD MMM YYYY h:mma')}{purchase.refundedBy ? ' by ' + purchase.refundedBy : ''}
                                                                </div>
                                                                : null}
                                                        </div>
                                                    </div>

                                                    <div className="mt-4 space-y-4">
                                                        <label>
                                                            <div className="text-sm font-semibold text-slate-900 dark:text-white">Phone</div>
                                                            <input className={inputClass} type="tel" value={purchase.phone || ''} onChange={(e) => this._updatePurchaseField(key, 'phone', e.target.value)} />
                                                        </label>
                                                        <label>
                                                            <div className="text-sm font-semibold text-slate-900 dark:text-white">Email</div>
                                                            <input className={inputClass} type="email" value={purchase.email || ''} onChange={(e) => this._updatePurchaseField(key, 'email', e.target.value)} />
                                                        </label>
                                                        <div>
                                                            <div className="text-sm font-semibold text-slate-900 dark:text-white">Address</div>
                                                            <div className="mt-2 space-y-3">
                                                                <input className={inputClass + ' mt-0'} placeholder="Street number" value={purchase.addressNumber || ''} onChange={(e) => this._updatePurchaseField(key, 'addressNumber', e.target.value)} />
                                                                <input className={inputClass + ' mt-0'} placeholder="Street" value={purchase.addressStreet || ''} onChange={(e) => this._updatePurchaseField(key, 'addressStreet', e.target.value)} />
                                                                <input className={inputClass + ' mt-0'} placeholder="Suburb" value={purchase.addressSuburb || ''} onChange={(e) => this._updatePurchaseField(key, 'addressSuburb', e.target.value)} />
                                                                <div className="grid gap-3 sm:grid-cols-2">
                                                                    <select className={inputClass + ' mt-0'} value={(purchase.addressState || '').toUpperCase()} onChange={(e) => this._updatePurchaseField(key, 'addressState', e.target.value)}>
                                                                        <option value="">State</option>
                                                                        <option value="ACT">ACT</option>
                                                                        <option value="NSW">NSW</option>
                                                                        <option value="NT">NT</option>
                                                                        <option value="QLD">QLD</option>
                                                                        <option value="SA">SA</option>
                                                                        <option value="TAS">TAS</option>
                                                                        <option value="VIC">VIC</option>
                                                                        <option value="WA">WA</option>
                                                                    </select>
                                                                    <input className={inputClass + ' mt-0'} inputMode="numeric" placeholder="Postcode" value={purchase.addressPostcode || ''} onChange={(e) => this._updatePurchaseField(key, 'addressPostcode', e.target.value)} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <label className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
                                                            <input type="checkbox" checked={!!purchase.optIn} onChange={(e) => this._updatePurchaseField(key, 'optIn', e.target.checked)} />
                                                            Opted in
                                                        </label>
                                                        <button className="inline-flex w-full items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-sky-500 dark:text-slate-950 dark:hover:bg-sky-400" disabled={this.state.savingPurchaseKey === key} onClick={() => this._savePurchase(key)}>
                                                            {this.state.savingPurchaseKey === key ? 'Saving...' : 'Save purchase'}
                                                        </button>
                                                        {eventDrawn ?
                                                            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                                                                Refund unavailable after draw
                                                            </div>
                                                            :
                                                            <button
                                                                className="inline-flex w-full items-center justify-center rounded-2xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/15"
                                                                disabled={this.state.refundingPurchaseKey === key || !!this._refundLabel(purchase)}
                                                                onClick={() => this._refundPurchase(key)}
                                                            >
                                                                {this.state.refundingPurchaseKey === key ? 'Refunding...' : (this._refundLabel(purchase) ? 'Refunded' : 'Refund purchase')}
                                                            </button>}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                            </section>
                            : null}
                    </div>
                </main>
                <PublicPreviewModal
                    open={this.state.previewOpen}
                    client={client}
                    event={event}
                    onClose={() => this.setState({ previewOpen: false })}
                />
                <div className="fixed bottom-4 right-4 z-40">
                    <div className="flex items-center gap-2 rounded-full border border-slate-200/80 bg-white/92 p-2 shadow-2xl backdrop-blur dark:border-slate-800 dark:bg-slate-950/92">
                        <button
                            type="button"
                            className={'inline-flex items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition hover:bg-slate-50 dark:hover:bg-slate-900 ' + (isDirty
                                ? 'border-[#1cd997] text-[#0f766e] dark:text-[#1cd997]'
                                : 'border-slate-300 text-slate-700 dark:border-slate-700 dark:text-slate-200')}
                            onClick={() => this.setState({ previewOpen: true })}
                        >
                            {isDirty ?
                                <span
                                    aria-hidden="true"
                                    className="inline-block h-2 w-2 rounded-full bg-[#1cd997] shadow-[0_0_0_3px_rgba(28,217,151,0.18)]"
                                />
                                : null}
                            Preview
                        </button>
                        <button
                            type="button"
                            disabled={!isDirty || this.state.savingEvent}
                            className="inline-flex items-center justify-center rounded-full px-3 py-2 text-xs font-semibold text-slate-950 transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
                            style={{ background: '#1cd997' }}
                            onClick={() => this._saveEvent()}
                        >
                            {this.state.savingEvent ?
                                <span className="inline-flex items-center gap-2">
                                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-950/25 border-t-slate-950" />
                                    Saving...
                                </span>
                                :
                                'Save event'}
                        </button>
                    </div>
                </div>
            </div>
        )
    }
}

export default withRouter(EditEvent)
